import { Column, Table } from '@tanstack/react-table';
import { Reorder } from 'framer-motion';
import { isFunction, sortBy } from 'lodash';
import { List } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { DragItem } from '@/components/ui/drag-item';
import { Drawer } from '@/components/ui/drawer';
import { DrawerHeader } from '@/components/ui/drawer/header';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { tv } from '@/utils/styles';

export interface ShipmentTableSettingsDrawerProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  table: Table<ShipmentModel>;
}

const PLACEHOLDER_TEXT = 'Drag items here';

const ShipmentTableSettingsDrawer = ({ isOpen, onOpenChange, table }: ShipmentTableSettingsDrawerProps) => {
  const [visibleItems, setVisibleItems] = useState<Column<ShipmentModel>[]>([]);
  const [hiddenItems, setHiddenItems] = useState<Column<ShipmentModel>[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const visibleRef = useRef<HTMLUListElement>(null);
  const hiddenRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isDragging) {
      visibleItems.map((column) => column.toggleVisibility(true));
      table.setColumnOrder(visibleItems.map((item) => item.id));
    }
  }, [isDragging]);

  useEffect(() => {
    if (isOpen) {
      const columns = table
        .getAllColumns()
        .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide());
      const tempVisible = columns.filter((column) => column.getIsVisible());

      const { columnOrder } = table.getState();
      setVisibleItems(sortBy(tempVisible, (item) => columnOrder.indexOf(item.id)));
      setHiddenItems(columns.filter((column) => !column.getIsVisible()));
    }
  }, [isOpen]);

  useEffect(() => {
    hiddenItems.map((column) => column.toggleVisibility(false));
  }, [hiddenItems]);

  const isPointInBoundingBox = (point: { x: number; y: number }, ref: React.RefObject<HTMLUListElement>): boolean => {
    if (!ref.current) return false;

    const rect = ref.current.getBoundingClientRect();
    return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
  };

  const moveItem = (
    sourceList: Column<ShipmentModel>[],
    setSourceList: React.Dispatch<React.SetStateAction<Column<ShipmentModel>[]>>,
    targetList: Column<ShipmentModel>[],
    setTargetList: React.Dispatch<React.SetStateAction<Column<ShipmentModel>[]>>,
    item: Column<ShipmentModel>,
  ) => {
    const newSourceList = sourceList.filter((i) => i.id !== item.id);
    setSourceList(newSourceList);
    setTargetList([...targetList, item]);
  };

  const { base, dragArea, placeholder } = styles();
  return (
    <Drawer width="420px" variant="single" closeDrawer={() => onOpenChange(false)} isOpen={isOpen}>
      <div className={base()}>
        <DrawerHeader title="Columns" icon={<List size={16} />} onClose={() => onOpenChange(false)} />
        <Text className="text-baseSm">Show, hide or change the column order by dragging the items below</Text>
        <div className="flex flex-col gap-2">
          <div>
            <Heading variant="h4">Visible</Heading>
            <Reorder.Group
              ref={visibleRef}
              axis="y"
              values={visibleItems}
              onReorder={setVisibleItems}
              className={dragArea({ isDragging })}
            >
              {visibleItems.map((item) => {
                return (
                  <DragItem
                    key={item.id}
                    value={item}
                    drag
                    dragConstraints={false}
                    dragElastic={0}
                    dragMomentum={false}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={(_, info) => {
                      setIsDragging(false);
                      if (isPointInBoundingBox(info.point, hiddenRef)) {
                        item.toggleVisibility(false);
                        moveItem(visibleItems, setVisibleItems, hiddenItems, setHiddenItems, item);
                      }
                    }}
                  >
                    {!isFunction(item.columnDef.header) ? item.columnDef.header : item.id}
                  </DragItem>
                );
              })}
              <div className={placeholder({ className: visibleItems.length ? 'mt-2' : '', isDragging })}>
                {PLACEHOLDER_TEXT}
              </div>
            </Reorder.Group>
          </div>
          <div>
            <Heading variant="h4">Hidden</Heading>
            <Reorder.Group
              ref={hiddenRef}
              axis="y"
              values={hiddenItems}
              onReorder={setHiddenItems}
              className={dragArea()}
            >
              {hiddenItems.map((item) => (
                <DragItem
                  key={item.id}
                  value={item}
                  drag
                  dragConstraints={false}
                  dragElastic={0}
                  dragMomentum={false}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={(_, info) => {
                    setIsDragging(false);
                    if (isPointInBoundingBox(info.point, visibleRef)) {
                      moveItem(hiddenItems, setHiddenItems, visibleItems, setVisibleItems, item);
                    }
                  }}
                >
                  {!isFunction(item.columnDef.header) ? item.columnDef.header : item.id}
                </DragItem>
              ))}
              <div className={placeholder({ className: hiddenItems.length ? 'mt-2' : '', isDragging })}>
                {PLACEHOLDER_TEXT}
              </div>
            </Reorder.Group>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

const styles = tv({
  slots: {
    base: 'p-4',
    dragArea: 'flex flex-col gap-1 rounded-md border border-dashed border-grey-350 p-2',
    placeholder: 'w-full rounded-md bg-grey-100 p-2 text-center text-sm text-text-secondary hover:bg-grey-200',
  },
  variants: {
    isDragging: {
      true: {
        placeholder: 'hover:bg-grey-200',
      },
    },
  },
});

export { ShipmentTableSettingsDrawer };
